import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import useImageLoader from './useImageLoader';
import OnlineOpeningRollModal from './OnlineOpeningRollModal';
import { API_BASE_URL } from './api';
import { getMovesForPiece, getMovesForDestination, getMovablePoints } from './gameEngine';

/* eslint-disable react-hooks/exhaustive-deps */

const CANVAS_W = 900;
const CANVAS_H = 900;
const DEFAULT_CHECKER_SIZE = 56;
const POINT_STACK_HEIGHT = 336;

function position(x, y) {
  let cx = 88;
  let cy = 161;
  let px = x;
  if (px >= 6) px += 1;
  if (y >= 6) cy += 22;
  return [cx + px * 56, cy + y * 56];
}

function getPointStackLayout(location, pieceCount = 0) {
  const posX = location < 13 ? 12 - location : location - 1 - 12;
  const [baseX] = position(posX, location < 13 ? 0 : 11);
  const safeCount = Math.max(1, pieceCount);
  const pieceSize = Math.min(DEFAULT_CHECKER_SIZE, Math.floor(POINT_STACK_HEIGHT / safeCount));
  const positions = [];

  if (location < 13) {
    const startY = position(posX, 0)[1];
    for (let i = 0; i < pieceCount; i += 1) {
      positions.push([baseX, startY + (i * pieceSize)]);
    }
  } else {
    const bottomY = position(posX, 11)[1];
    for (let i = 0; i < pieceCount; i += 1) {
      positions.push([baseX, bottomY - (i * pieceSize)]);
    }
  }

  return { positions, pieceSize };
}

function getBarPositionsWhite() {
  const positions = [];
  for (let i = 0; i < 6; i++) positions.push([426, 450 + i * 56]);
  return positions;
}

function getBarPositionsBlack() {
  const positions = [];
  for (let i = 0; i < 6; i++) positions.push([426, 161 + i * 56]);
  return positions;
}

function getBearingOffPositions(color) {
  const positions = [];
  const bx = 840;
  const by = color === 'white' ? 595 : 159;
  if (color === 'white') {
    for (let i = 0; i < 15; i++) positions.push([bx, by + 18 * i]);
  } else {
    for (let i = 14; i >= 0; i--) positions.push([bx, by + 18 * i]);
  }
  return positions;
}

function getPointLightPosition(location) {
  if (location < 13) return position(12 - location, 0);
  return position(location - 1 - 12, 7);
}

function clampStackIndex(index) {
  return Math.max(0, index);
}

function getVisibleStackWindow(pieceCount) {
  return { startIndex: 0, visibleCount: pieceCount };
}

function getDefaultReconnectState() {
  return {
    paused: false,
    disconnectedColor: null,
    disconnectedUserId: null,
    reconnectDeadline: null,
    graceMs: 300000,
  };
}

function getDefaultSessionState() {
  return {
    targetWins: 1,
    wins: { white: 0, black: 0 },
    roundNumber: 1,
    awaitingNextRound: false,
  };
}

function formatReconnectCountdown(ms) {
  const safeMs = Math.max(0, Number(ms) || 0);
  const totalSeconds = Math.ceil(safeMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatTurnElapsed(ms) {
  const safeMs = Math.max(0, Number(ms) || 0);
  const totalSeconds = Math.floor(safeMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function getVisibleDiceState(gameState) {
  const gs = gameState || {};
  const dice = Array.isArray(gs.dice) ? gs.dice : [];
  const diceUsed = Array.isArray(gs.diceUsed) ? gs.diceUsed : [];
  const isDouble = dice.length >= 2 && dice[0] === dice[1];
  const remainingMoves = isDouble
    ? diceUsed.filter((used) => !used).length
    : diceUsed.slice(0, 2).filter((used) => !used).length;

  return {
    first: Number(dice[0] || 0),
    second: Number(dice[1] || 0),
    isDouble,
    firstUsed: !isDouble && Boolean(diceUsed[0]),
    secondUsed: !isDouble && Boolean(diceUsed[1]),
    remainingMoves,
  };
}

function renderDieFace(value) {
  const safe = Math.max(1, Math.min(6, Number(value) || 1));
  const positionsByValue = {
    1: [[50, 50]],
    2: [[25, 25], [75, 75]],
    3: [[25, 25], [50, 50], [75, 75]],
    4: [[25, 25], [75, 25], [25, 75], [75, 75]],
    5: [[25, 25], [75, 25], [50, 50], [25, 75], [75, 75]],
    6: [[25, 25], [75, 25], [25, 50], [75, 50], [25, 75], [75, 75]],
  };

  return positionsByValue[safe].map(([col, row], index) => (
    <span
      key={`${safe}-${col}-${row}-${index}`}
      style={{
        ...styles.dicePip,
        left: `calc(${col}% - 4px)`,
        top: `calc(${row}% - 4px)`,
      }}
    />
  ));
}

export default function OnlineMatchGame({
  token,
  user,
  matchInfo,
  onMatchSwitch,
  onExitMatch,
  onWalletChange,
  isFullscreen,
  onToggleFullscreen,
}) {
  const canvasRef = useRef(null);
  const socketRef = useRef(null);
  const chatScrollRef = useRef(null);
  const matchInfoRef = useRef(matchInfo);
  const onWalletChangeRef = useRef(onWalletChange);
  const mobileTabRef = useRef('game');
  const mobileChatHistoryActiveRef = useRef(false);
  const dismissedOpeningRollRef = useRef(null);
  const suppressOpeningRollTokenRef = useRef(matchInfo?.resumed ? matchInfo?.state?.openingRoll?.token || null : null);
  const previousDiceRolledRef = useRef(Boolean(matchInfo?.state?.diceRolled));
  const { images, loaded } = useImageLoader();

  const [gameState, setGameState] = useState(matchInfo?.state || null);
  const [selectedFrom, setSelectedFrom] = useState(null);
  const [notice, setNotice] = useState('');
  const [stillPrompt, setStillPrompt] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [roundResult, setRoundResult] = useState(null);
  const [quitConfirm, setQuitConfirm] = useState(false);
  const [isForfeiting, setIsForfeiting] = useState(false);
  const [incomingReq, setIncomingReq] = useState(null);
  const [isSwitchingMatches, setIsSwitchingMatches] = useState(false);
  const isSwitchingMatchesRef = useRef(false);
  const [openingRollModal, setOpeningRollModal] = useState(() => matchInfo?.state?.openingRoll || null);
  const [openingRollAnimating, setOpeningRollAnimating] = useState(false);
  const [moveAnimation, setMoveAnimation] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState(() => matchInfo?.chatHistory || []);
  const [presence, setPresence] = useState(() => matchInfo?.reconnect || getDefaultReconnectState());
  const [sessionInfo, setSessionInfo] = useState(() => matchInfo?.session || getDefaultSessionState());
  const sessionInfoRef = useRef(sessionInfo);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  const [mobileTab, setMobileTab] = useState('game');
  const [unseenChatCount, setUnseenChatCount] = useState(0);
  const [showResumeOverlay, setShowResumeOverlay] = useState(() => Boolean(matchInfo?.resumed));
  const [reconnectNow, setReconnectNow] = useState(() => Date.now());
  const [turnClockNow, setTurnClockNow] = useState(() => Date.now());
  const [diceTrayRolling, setDiceTrayRolling] = useState(false);
  const [lastShownDice, setLastShownDice] = useState(() => {
    const initialDice = Array.isArray(matchInfo?.state?.dice) ? matchInfo.state.dice : [];
    return {
      first: Number(initialDice[0] || 1),
      second: Number(initialDice[1] || 1),
      hasValue: Boolean(matchInfo?.state?.diceRolled && initialDice.length >= 2),
    };
  });

  useEffect(() => {
    matchInfoRef.current = matchInfo;
  }, [matchInfo]);

  useEffect(() => {
    sessionInfoRef.current = sessionInfo;
  }, [sessionInfo]);

  useEffect(() => {
    isSwitchingMatchesRef.current = Boolean(isSwitchingMatches);
  }, [isSwitchingMatches]);

  function roundToTwo(value) {
    return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
  }

  function formatCoins(value) {
    const n = roundToTwo(value);
    return n % 1 === 0 ? String(Math.trunc(n)) : String(n);
  }

  const assignedColor = matchInfo?.color;
  const boardFlipped = false;
  const localPlayerId = user?.id || '';
  const opponent = matchInfo?.opponent || {};
  const isSpectatorMode = assignedColor !== 'white' && assignedColor !== 'black';
  const isMobileViewport = viewportWidth < 900;
  const currentTurnColor = gameState?.currentPlayer || null;
  const activePlayerId = !currentTurnColor
    ? ''
    : currentTurnColor === assignedColor
      ? localPlayerId
      : (opponent.id || '');
  const moveDirectionUsedInValidation = currentTurnColor === 'white'
    ? 'decreasing'
    : currentTurnColor === 'black'
      ? 'increasing'
      : 'unknown';
  const isOpponentReconnectPause = presence?.paused && presence?.disconnectedUserId === opponent.id;
  const myWins = Number(sessionInfo?.wins?.[assignedColor] || 0);
  const opponentColor = assignedColor === 'white' ? 'black' : 'white';
  const opponentWins = Number(sessionInfo?.wins?.[opponentColor] || 0);
  const localDisplayName = user?.name || 'You';
  const whitePlayerName = assignedColor === 'white' ? localDisplayName : (opponent.name || 'Opponent');
  const blackPlayerName = assignedColor === 'black' ? localDisplayName : (opponent.name || 'Opponent');
  const matchStatusText = gameState?.openingRoll?.active
    ? 'Roll To Start'
    : gameState?.gamePhase === 'gameOver'
      ? 'Game Over'
      : gameState?.currentPlayer === assignedColor
        ? 'Your Turn'
        : `${opponent.name || 'Opponent'}'s Turn`;
  const reconnectCountdownMs = Math.max(0, Number(presence?.reconnectDeadline || 0) - reconnectNow);
  const reconnectCountdownLabel = formatReconnectCountdown(reconnectCountdownMs);
  const diceState = getVisibleDiceState(gameState);
  const canRollDice = Boolean(
    gameState
    && !matchResult
    && !isSpectatorMode
    && gameState.gamePhase === 'playing'
    && gameState.currentPlayer === assignedColor
    && !gameState.diceRolled
  );
  const visibleDiceValues = gameState?.diceRolled
    ? { first: diceState.first, second: diceState.second, blurred: false }
    : {
        first: lastShownDice.first,
        second: lastShownDice.second,
        blurred: canRollDice || lastShownDice.hasValue,
      };
  const diceTrayTitle = !gameState?.diceRolled
    ? (canRollDice ? 'Roll Dice' : 'Waiting For Roll')
    : (diceState.isDouble ? `Double ${diceState.first}` : 'Current Dice');
  const diceTrayTurnLabel = gameState?.gamePhase === 'playing'
    ? (gameState?.currentPlayer === assignedColor ? 'Your Turn' : `${opponent.name || 'Opponent'} Turn`)
    : matchStatusText;
  const turnElapsedMs = gameState?.gamePhase === 'playing' && gameState?.turnStartedAt
    ? Math.min(Number(gameState?.turnDurationMs || 300000), Math.max(0, turnClockNow - Number(gameState.turnStartedAt)))
    : 0;
  const turnElapsedLabel = `${formatTurnElapsed(turnElapsedMs)} / 5:00`;

  const pushIncomingChatMessage = useCallback((message) => {
    setChatMessages((prev) => {
      const incomingTime = Date.parse(message?.at || '') || Date.now();
      const pendingIndex = prev.findIndex((entry) => (
        entry?.pending
        && entry.fromUserId === message?.fromUserId
        && entry.text === message?.text
        && Math.abs((Date.parse(entry.at || '') || incomingTime) - incomingTime) < 15000
      ));
      if (pendingIndex >= 0) {
        const next = [...prev];
        next[pendingIndex] = message;
        return next;
      }
      return [...prev, message];
    });
  }, []);

  const openMobileGameTab = useCallback(() => {
    if (typeof window !== 'undefined' && mobileChatHistoryActiveRef.current) {
      const nextState = { ...(window.history.state || {}) };
      delete nextState.onlineMatchMobileChat;
      window.history.replaceState(nextState, document.title, window.location.href);
      mobileChatHistoryActiveRef.current = false;
    }
    setMobileTab('game');
    setUnseenChatCount(0);
  }, []);

  const openMobileChatTab = useCallback(() => {
    if (
      typeof window !== 'undefined'
      && isMobileViewport
      && !mobileChatHistoryActiveRef.current
    ) {
      window.history.pushState(
        { ...(window.history.state || {}), onlineMatchMobileChat: true },
        document.title,
        window.location.href
      );
      mobileChatHistoryActiveRef.current = true;
    }
    setMobileTab('chat');
    setUnseenChatCount(0);
  }, [isMobileViewport]);

  useEffect(() => {
    mobileTabRef.current = mobileTab;
  }, [mobileTab]);

  useEffect(() => {
    function handlePopState() {
      if (mobileTabRef.current === 'chat') {
        mobileChatHistoryActiveRef.current = false;
        setMobileTab('game');
        setUnseenChatCount(0);
      }
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    onWalletChangeRef.current = onWalletChange;
  }, [onWalletChange]);

  useEffect(() => {
    const opening = gameState?.openingRoll;
    if (!opening) {
      setOpeningRollModal(null);
      return;
    }
    if (opening.token && suppressOpeningRollTokenRef.current === opening.token) {
      setOpeningRollModal(null);
      return;
    }
    if (showResumeOverlay) {
      return;
    }
    if (opening.active || dismissedOpeningRollRef.current !== opening.token) {
      setOpeningRollModal(opening);
    }
  }, [gameState?.openingRoll, showResumeOverlay]);

  useEffect(() => {
    if (!openingRollModal || openingRollModal.active || !openingRollModal.starter) return undefined;
    const timer = setTimeout(() => {
      if (openingRollModal?.token) {
        dismissedOpeningRollRef.current = openingRollModal.token;
      }
      setOpeningRollAnimating(false);
      setOpeningRollModal(null);
    }, 5000);
    return () => clearTimeout(timer);
  }, [openingRollModal]);

  useEffect(() => {
    if (!openingRollModal?.active) return;
    const myOpeningRoll = assignedColor ? openingRollModal?.[assignedColor] : null;
    if (!Number.isInteger(myOpeningRoll)) {
      setOpeningRollAnimating(false);
    }
  }, [openingRollModal, assignedColor]);

  useEffect(() => {
    function handleResize() {
      setViewportWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const socket = io(API_BASE_URL, {
      auth: { token },
      transports: ['websocket'],
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setNotice('');
      const roomId = matchInfoRef.current?.roomId;
      if (roomId) socket.emit('online:join-match', { roomId });
    });

    socket.on('disconnect', () => {
      setStillPrompt(false);
      setOpeningRollAnimating(false);
    });

    socket.on('online:request-received', ({ from, stake: reqStake, targetWins: reqTargetWins, matchMode: reqMatchMode }) => {
      const isFriendlyMatch = Number(matchInfoRef.current?.stake || 0) === 0;
      if (!isFriendlyMatch) return;
      if (reqMatchMode === 'friendly') return;
      setIncomingReq({ ...from, stake: reqStake, targetWins: reqTargetWins || 1, matchMode: reqMatchMode });
    });

    socket.on('online:request-error', ({ message }) => {
      setIsSwitchingMatches(false);
      setIncomingReq(null);
      setNotice(message || 'Request failed');
    });

    socket.on('online:match-start', (payload) => {
      if (!payload?.roomId) return;
      if (payload.roomId === matchInfoRef.current?.roomId) return;
      setIncomingReq(null);
      setIsSwitchingMatches(false);
      if (typeof onMatchSwitch === 'function') {
        onMatchSwitch(payload);
      }
    });

    socket.on('online:match-state', (state) => {
      setGameState((prev) => {
        const animation = inferMoveAnimation(prev, state);
        if (animation) {
          setMoveAnimation(animation);
        }
        return state;
      });
      const liveAssignedColor = matchInfoRef.current?.color;
      const myOpeningRoll = liveAssignedColor ? state?.openingRoll?.[liveAssignedColor] : null;
      if (!state?.openingRoll?.active || Number.isInteger(myOpeningRoll)) {
        setOpeningRollAnimating(false);
      }
      setSelectedFrom(null);
      if (state?.gamePhase === 'playing') {
        setRoundResult(null);
      }
    });

    socket.on('online:match-presence', (nextPresence) => {
      const normalized = nextPresence || getDefaultReconnectState();
      setPresence(normalized);
      if (!normalized.paused && socket.connected) {
        setNotice('');
      }
    });

    socket.on('online:action-error', ({ message }) => {
      setOpeningRollAnimating(false);
      setIsForfeiting(false);
      setNotice(message || 'Action failed');
    });

    socket.on('online:still-playing-prompt', () => {
      setStillPrompt(true);
    });

    socket.on('online:match-ended', ({ winnerId, reason, stake, winnerDelta, loserDelta }) => {
      setIsForfeiting(false);
      if (Number(matchInfoRef.current?.stake || 0) === 0 && isSwitchingMatchesRef.current && reason === 'opponent-left') {
        return;
      }
      const iWon = winnerId === user.id;
      const resultCode = winnerId ? (iWon ? 'win' : 'loss') : 'draw';
      let subtitle = reason || 'completed';
      if (reason === 'timeout-no-response' || reason === 'timeout-final') {
        subtitle = iWon ? 'You won by timeout' : 'You lost by timeout';
      } else if (reason === 'disconnect-timeout') {
        subtitle = iWon ? 'You won because your opponent did not reconnect in time' : 'You did not reconnect within the grace window';
      } else if (reason === 'opponent-left') {
        const liveOpponent = matchInfoRef.current?.opponent || opponent;
        subtitle = iWon ? 'You won: opponent left the game' : `${liveOpponent.name} stopped playing`;
      } else if (reason === 'round-forfeit') {
        subtitle = iWon ? 'You won because your opponent forfeited this round' : 'You forfeited this round';
      } else if (reason === 'completed') {
        subtitle = iWon ? 'You won the match' : 'You lost the match';
      }
      const myDelta = iWon ? (winnerDelta ?? 0) : (loserDelta ?? 0);
      setMatchResult({
        title: iWon ? 'Victory' : 'Defeat',
        subtitle: `${subtitle}${stake ? ` | Stake: ${stake} | Coin Change: ${myDelta >= 0 ? '+' : ''}${myDelta}` : ''}`,
        payload: {
          returnToPlatform: Boolean(user?.isExternal),
          status: 'completed',
          result: resultCode,
          reason: reason || 'completed',
          stake: stake || 0,
          coinChange: myDelta,
          opponentId: (matchInfoRef.current?.opponent || opponent).id || '',
          opponentName: (matchInfoRef.current?.opponent || opponent).name || '',
        },
      });
      if (typeof onWalletChangeRef.current === 'function') onWalletChangeRef.current();
    });

    socket.on('online:round-ended', ({ winnerId, winnerName, reason, session, stake, winnerDelta, loserDelta }) => {
      if (session) setSessionInfo(session);
      const liveAssignedColor = matchInfoRef.current?.color;
      const liveOpponent = matchInfoRef.current?.opponent || opponent;
      const liveOpponentColor = liveAssignedColor === 'white' ? 'black' : liveAssignedColor === 'black' ? 'white' : '';
      const iWon = winnerId === user.id;
      const myDelta = iWon ? (winnerDelta ?? 0) : (loserDelta ?? 0);
      const target = Number(session?.targetWins || sessionInfoRef.current?.targetWins || 1);
      const nextMyWins = Number(session?.wins?.[liveAssignedColor] || 0);
      const nextOpponentWins = Number(session?.wins?.[liveOpponentColor] || 0);
      if (session?.sessionComplete) return;
      setRoundResult({
        title: `Round ${session?.roundNumber || 1} Complete`,
        winnerName: iWon ? 'You' : (winnerName || liveOpponent.name || 'Opponent'),
        subtitle: `Series score: You ${nextMyWins} - ${nextOpponentWins} ${liveOpponent.name || 'Opponent'} | First to ${target}${stake ? ` | Coin Change: ${myDelta >= 0 ? '+' : ''}${myDelta}` : ''}`,
      });
      setNotice('');
    });

    socket.on('online:round-forfeit-announced', ({ winnerId, winnerName, loserId, session }) => {
      setIsForfeiting(false);
      if (session) setSessionInfo(session);
      const liveAssignedColor = matchInfoRef.current?.color;
      const liveOpponent = matchInfoRef.current?.opponent || opponent;
      const liveOpponentColor = liveAssignedColor === 'white' ? 'black' : liveAssignedColor === 'black' ? 'white' : '';
      const iWon = winnerId === user.id;
      const forfeiterIsMe = loserId === user.id;
      const nextMyWins = Number(session?.wins?.[liveAssignedColor] || 0);
      const nextOpponentWins = Number(session?.wins?.[liveOpponentColor] || 0);
      if (session?.sessionComplete) {
        setNotice(
          forfeiterIsMe
            ? 'You forfeited the deciding round. Final match result is being prepared.'
            : `${liveOpponent.name || 'Opponent'} forfeited the deciding round. Final match result is being prepared.`
        );
        return;
      }
      setRoundResult({
        title: `Round ${session?.roundNumber || 1} Complete`,
        winnerName: iWon ? 'You' : (winnerName || liveOpponent.name || 'Opponent'),
        subtitle: `${
          forfeiterIsMe
            ? 'You forfeited this round.'
            : `${liveOpponent.name || 'Opponent'} forfeited this round.`
        } Series score: You ${nextMyWins} - ${nextOpponentWins} ${liveOpponent.name || 'Opponent'}`,
      });
      setNotice('');
    });

    socket.on('online:session-update', (nextSession) => {
      setSessionInfo(nextSession || getDefaultSessionState());
      if (nextSession && nextSession.awaitingNextRound === false) {
        setRoundResult(null);
      }
    });

    socket.on('online:chat-message', (msg) => {
      pushIncomingChatMessage(msg);
      if (typeof window !== 'undefined' && window.innerWidth < 900 && msg?.fromUserId !== user.id) {
        setUnseenChatCount((prev) => (mobileTabRef.current === 'chat' ? prev : prev + 1));
      }
    });

    socket.on('online:chat-history', (messages) => {
      setChatMessages(Array.isArray(messages) ? messages : []);
    });

    return () => {
      socket.disconnect();
    };
  }, [token, user.id, user?.isExternal, pushIncomingChatMessage, onMatchSwitch]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !matchInfo?.roomId) return;
    if (socket.connected) {
      socket.emit('online:join-match', { roomId: matchInfo.roomId });
    }
  }, [matchInfo?.roomId]);

  useEffect(() => {
    setGameState(matchInfo?.state || null);
    setOpeningRollModal(matchInfo?.state?.openingRoll || null);
    setMatchResult(null);
    setRoundResult(null);
    setQuitConfirm(false);
    setIncomingReq(null);
    setIsSwitchingMatches(false);
    setNotice('');
  }, [matchInfo?.roomId]);

  function respondIncomingRequest(accepted) {
    if (!incomingReq?.id) return;
    setNotice('');
    setQuitConfirm(false);
    if (accepted) {
      setIsSwitchingMatches(true);
    } else {
      setIsSwitchingMatches(false);
      setIncomingReq(null);
    }
    socketRef.current?.emit('online:respond-request', { fromUserId: incomingReq.id, accepted: Boolean(accepted) });
  }

  useEffect(() => {
    setSelectedFrom(null);
  }, [matchInfo?.roomId, assignedColor]);

  useEffect(() => {
    setIsForfeiting(false);
  }, [matchInfo?.roomId]);

  useEffect(() => {
    setPresence(matchInfo?.reconnect || getDefaultReconnectState());
  }, [matchInfo?.roomId, matchInfo?.reconnect]);

  useEffect(() => {
    if (!presence?.paused || !presence?.reconnectDeadline) return undefined;
    setReconnectNow(Date.now());
    const timer = setInterval(() => {
      setReconnectNow(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, [presence?.paused, presence?.reconnectDeadline]);

  useEffect(() => {
    if (gameState?.gamePhase !== 'playing' || !gameState?.turnStartedAt) return undefined;
    setTurnClockNow(Date.now());
    const timer = setInterval(() => {
      setTurnClockNow(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, [gameState?.gamePhase, gameState?.turnStartedAt, gameState?.currentPlayer]);

  useEffect(() => {
    if (!diceTrayRolling) return undefined;
    const timer = setTimeout(() => setDiceTrayRolling(false), 900);
    return () => clearTimeout(timer);
  }, [diceTrayRolling]);

  useEffect(() => {
    if (gameState?.diceRolled && diceState.first > 0 && diceState.second > 0) {
      setLastShownDice({
        first: diceState.first,
        second: diceState.second,
        hasValue: true,
      });
    }
  }, [gameState?.diceRolled, diceState.first, diceState.second]);

  useEffect(() => {
    previousDiceRolledRef.current = Boolean(gameState?.diceRolled);
  }, [gameState?.diceRolled]);

  useEffect(() => {
    setShowResumeOverlay(Boolean(matchInfo?.resumed));
    suppressOpeningRollTokenRef.current = matchInfo?.resumed ? matchInfo?.state?.openingRoll?.token || null : null;
  }, [matchInfo?.roomId, matchInfo?.resumed]);

  useEffect(() => {
    setSessionInfo(matchInfo?.session || getDefaultSessionState());
  }, [matchInfo?.roomId, matchInfo?.session]);

  useEffect(() => {
    setChatMessages(matchInfo?.chatHistory || []);
    setChatInput('');
    setMobileTab('game');
    setUnseenChatCount(0);
    mobileChatHistoryActiveRef.current = false;
  }, [matchInfo?.roomId, matchInfo?.chatHistory]);

  useEffect(() => {
    if (!showResumeOverlay) return undefined;
    const timer = setTimeout(() => setShowResumeOverlay(false), 3000);
    return () => clearTimeout(timer);
  }, [showResumeOverlay]);

  useEffect(() => {
    if (!isMobileViewport) {
      setMobileTab('game');
      setUnseenChatCount(0);
      mobileChatHistoryActiveRef.current = false;
    }
  }, [isMobileViewport]);

  useEffect(() => {
    if (!chatScrollRef.current) return;
    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [chatMessages]);

  useEffect(() => {
    if (!moveAnimation) return undefined;
    const timer = setTimeout(() => setMoveAnimation(null), moveAnimation.duration);
    return () => clearTimeout(timer);
  }, [moveAnimation]);

  useEffect(() => {
    console.debug('[online-match:state]', {
      activePlayerId,
      localPlayerId,
      assignedColor,
      currentTurnColor,
      boardFlipped,
      moveDirectionUsedInValidation,
      isSpectatorMode,
    });
  }, [activePlayerId, localPlayerId, assignedColor, currentTurnColor, boardFlipped, moveDirectionUsedInValidation, isSpectatorMode]);

  const mapPointForView = useCallback((location) => {
    if (typeof location !== 'number') return location;
    // Show the current user's side from the familiar bottom-right perspective.
    // Black uses the native layout; white is mirrored into the same viewpoint.
    return assignedColor === 'white' ? 25 - location : location;
  }, [assignedColor]);

  const isBottomSideColor = useCallback((color) => {
    if (color !== 'white' && color !== 'black') return false;
    return color === assignedColor;
  }, [assignedColor]);

  const getBarPositionsForView = useCallback((color) => (
    isBottomSideColor(color) ? getBarPositionsWhite() : getBarPositionsBlack()
  ), [isBottomSideColor]);

  const getBearingOffPositionsForView = useCallback((color) => (
    isBottomSideColor(color) ? getBearingOffPositions('white') : getBearingOffPositions('black')
  ), [isBottomSideColor]);

  const getCheckerCoords = useCallback((state, color, location, stackIndex = 0) => {
    if (!state) return [0, 0];
    if (location === 'bar') {
      const positions = getBarPositionsForView(color);
      const [x, y] = positions[clampStackIndex(stackIndex)] || positions[0];
      return [x, y, DEFAULT_CHECKER_SIZE];
    }
    if (location === 'off') {
      const positions = getBearingOffPositionsForView(color);
      const [x, y] = positions[Math.min(stackIndex, positions.length - 1)] || positions[0];
      return [x, y, DEFAULT_CHECKER_SIZE];
    }
    const pointCount = state?.points?.[location]?.pieces?.length || 0;
    const { positions, pieceSize } = getPointStackLayout(mapPointForView(location), pointCount);
    const [x, y] = positions[clampStackIndex(stackIndex)] || positions[0] || [0, 0];
    return [x, y, pieceSize];
  }, [getBarPositionsForView, getBearingOffPositionsForView, mapPointForView]);

  const inferMoveAnimation = useCallback((prevState, nextState) => {
    if (!prevState || !nextState) return null;

    const color = prevState.currentPlayer;
    if (!color) return null;

    const pointDiffs = [];
    for (let loc = 1; loc <= 24; loc += 1) {
      const prevCount = prevState.points?.[loc]?.pieces?.filter((piece) => piece.color === color).length || 0;
      const nextCount = nextState.points?.[loc]?.pieces?.filter((piece) => piece.color === color).length || 0;
      if (prevCount !== nextCount) pointDiffs.push({ loc, prevCount, nextCount });
    }

    const prevBarCount = prevState.bar?.[color]?.length || 0;
    const nextBarCount = nextState.bar?.[color]?.length || 0;
    const prevOffCount = prevState.borneOff?.[color]?.length || 0;
    const nextOffCount = nextState.borneOff?.[color]?.length || 0;

    let from = null;
    let to = null;
    let fromStackIndex = 0;
    let toStackIndex = 0;

    if (prevBarCount > nextBarCount) {
      from = 'bar';
      fromStackIndex = prevBarCount - 1;
    } else {
      const sourcePoint = pointDiffs.find((entry) => entry.prevCount > entry.nextCount);
      if (sourcePoint) {
        from = sourcePoint.loc;
        fromStackIndex = sourcePoint.prevCount - 1;
      }
    }

    if (nextOffCount > prevOffCount) {
      to = 'off';
      toStackIndex = nextOffCount - 1;
    } else {
      const targetPoint = pointDiffs.find((entry) => entry.nextCount > entry.prevCount);
      if (targetPoint) {
        to = targetPoint.loc;
        toStackIndex = targetPoint.nextCount - 1;
      }
    }

    if (from === null || to === null) return null;

    const [startX, startY, startSize] = getCheckerCoords(prevState, color, from, fromStackIndex);
    const [endX, endY, endSize] = getCheckerCoords(nextState, color, to, toStackIndex);

    return {
      color,
      to,
      startX,
      startY,
      startSize,
      endX,
      endY,
      endSize,
      startAt: performance.now(),
      duration: 650,
    };
  }, [getCheckerCoords]);

  const movablePoints = useMemo(() => (gameState?.diceRolled ? getMovablePoints(gameState) : new Set()), [gameState]);
  const ownedMovablePoints = useMemo(() => {
    if (!gameState || !assignedColor || gameState.currentPlayer !== assignedColor) return new Set();
    const allowed = new Set();
    movablePoints.forEach((point) => {
      if (point === 'bar') {
        if ((gameState.bar?.[assignedColor] || []).length > 0) {
          allowed.add(point);
        }
        return;
      }
      const topPiece = gameState.points?.[point]?.pieces?.[gameState.points?.[point]?.pieces.length - 1];
      if (topPiece?.color === assignedColor) {
        allowed.add(point);
      }
    });
    return allowed;
  }, [gameState, assignedColor, movablePoints]);
  const movesForSelected = useMemo(
    () => {
      if (!gameState || selectedFrom === null) return [];
      if (!assignedColor || gameState.currentPlayer !== assignedColor) return [];
      if (!ownedMovablePoints.has(selectedFrom)) return [];
      return getMovesForPiece(gameState, selectedFrom).filter((move) => {
        if (selectedFrom === 'bar') return (gameState.bar?.[assignedColor] || []).length > 0;
        const topPiece = gameState.points?.[selectedFrom]?.pieces?.[gameState.points?.[selectedFrom]?.pieces.length - 1];
        return topPiece?.color === assignedColor;
      });
    },
    [selectedFrom, gameState, assignedColor, ownedMovablePoints]
  );
  const destinationPoints = useMemo(() => new Set(movesForSelected.map((m) => m.to)), [movesForSelected]);

  function getCanvasCoords(e) {
    const canvas = canvasRef.current;
    if (!canvas) return [0, 0];
    const rect = canvas.getBoundingClientRect();
    const point = 'touches' in e && e.touches?.[0]
      ? e.touches[0]
      : 'changedTouches' in e && e.changedTouches?.[0]
        ? e.changedTouches[0]
        : e;
    return [
      (point.clientX - rect.left) * (CANVAS_W / rect.width),
      (point.clientY - rect.top) * (CANVAS_H / rect.height),
    ];
  }

  function inRect(mx, my, x1, x2, y1, y2, pad = 0) {
    return mx >= x1 - pad && mx <= x2 + pad && my >= y1 - pad && my <= y2 + pad;
  }

  function hitTestPoint(mx, my) {
    if (!gameState) return null;
    for (let loc = 1; loc <= 24; loc++) {
      const point = gameState.points[loc];
      if (point.pieces.length === 0) continue;
      const { positions, pieceSize } = getPointStackLayout(mapPointForView(loc), point.pieces.length);
      const { visibleCount } = getVisibleStackWindow(point.pieces.length);
      for (let slot = visibleCount - 1; slot >= 0; slot -= 1) {
        const [px, py] = positions[slot];
        if (inRect(mx, my, px, px + pieceSize, py, py + pieceSize, 4)) return loc;
      }
    }
    return null;
  }

  function hitTestBar(mx, my) {
    if (!gameState) return false;
    const color = gameState.currentPlayer;
    if (!color || gameState.bar[color].length === 0) return false;
    const barPos = getBarPositionsForView(color);
    for (let i = 0; i < Math.min(gameState.bar[color].length, 6); i++) {
      const [bx, by] = barPos[i];
      if (inRect(mx, my, bx, bx + 56, by, by + 56, 8)) return true;
    }
    return false;
  }

  function isWithinPointColumn(mx, my, point) {
    const displayPoint = mapPointForView(point);
    const lp = getPointLightPosition(displayPoint);
    const centerX = lp[0] + 28;
    const halfWidth = 42;
    if (displayPoint < 13) return mx >= centerX - halfWidth && mx <= centerX + halfWidth && my >= 145 && my <= 505;
    return mx >= centerX - halfWidth && mx <= centerX + halfWidth && my >= 395 && my <= 830;
  }

  function hitTestPointColumn(mx, my) {
    for (let point = 1; point <= 24; point++) {
      if (isWithinPointColumn(mx, my, point)) return point;
    }
    return null;
  }

  function hitTestDestination(mx, my) {
    if (!gameState) return null;
    for (const to of destinationPoints) {
      if (to === 'off') {
        if (isBottomSideColor(gameState.currentPlayer)) {
          if (mx >= 830 && mx <= 900 && my >= 580 && my <= 880) return 'off';
        } else if (mx >= 830 && mx <= 900 && my >= 140 && my <= 450) {
          return 'off';
        }
        continue;
      }
      if (isWithinPointColumn(mx, my, to)) return to;
    }
    return null;
  }

  const handleClick = useCallback((e) => {
    if (!gameState || matchResult) return;
    if (gameState.gamePhase === 'gameOver') return;
    if (isSpectatorMode) return;
    if (gameState.currentPlayer !== assignedColor) return;

    const [mx, my] = getCanvasCoords(e);
    const clickedPoint = hitTestPoint(mx, my);
    const clickedCheckerColor = clickedPoint !== null
      ? gameState.points?.[clickedPoint]?.pieces?.[gameState.points?.[clickedPoint]?.pieces.length - 1]?.color || null
      : null;

    console.debug('[online-match:click]', {
      activePlayerId,
      localPlayerId,
      assignedColor,
      checkerColorClicked: clickedCheckerColor,
      currentTurnColor,
      boardFlipped,
      moveDirectionUsedInValidation,
    });

      if (!gameState.diceRolled) {
      const isWhiteDiceArea = gameState.currentPlayer === 'white' && inRect(mx, my, 840, 885, 440, 560, 12);
      const isBlackDiceArea = gameState.currentPlayer === 'black' && inRect(mx, my, 3, 48, 420, 540, 12);
      if (isWhiteDiceArea || isBlackDiceArea) {
        socketRef.current?.emit('online:match-action', {
          roomId: matchInfo.roomId,
          type: 'roll',
        });
      }
      return;
    }

    if (selectedFrom !== null) {
      let destHit = hitTestDestination(mx, my);
      if (destHit === null) {
        const pointHitAsDest = hitTestPoint(mx, my);
        if (pointHitAsDest !== null && destinationPoints.has(pointHitAsDest)) {
          destHit = pointHitAsDest;
        }
      }
      if (destHit === null) {
        const columnHitAsDest = hitTestPointColumn(mx, my);
        if (columnHitAsDest !== null && destinationPoints.has(columnHitAsDest)) {
          destHit = columnHitAsDest;
        }
      }
      if (destHit !== null) {
        const possibleMoves = getMovesForDestination(gameState, selectedFrom, destHit);
        if (possibleMoves.length > 0) {
          socketRef.current?.emit('online:match-action', {
            roomId: matchInfo.roomId,
            type: 'move',
            move: {
              from: possibleMoves[0].from,
              to: possibleMoves[0].to,
              diceIndex: possibleMoves[0].diceIndex,
            },
          });
          setSelectedFrom(null);
          return;
        }
      }
    }

    if (hitTestBar(mx, my) && movablePoints.has('bar')) {
      if (assignedColor !== gameState.currentPlayer) return;
      setSelectedFrom('bar');
      return;
    }

    if (clickedPoint !== null) {
      const point = gameState.points[clickedPoint];
      const topPiece = point.pieces[point.pieces.length - 1];
      if (topPiece && topPiece.color === assignedColor && ownedMovablePoints.has(clickedPoint)) {
        setSelectedFrom(clickedPoint);
        return;
      }
      if (topPiece && topPiece.color !== assignedColor) {
        setSelectedFrom(null);
        return;
      }
    }

    const pointColumnHit = hitTestPointColumn(mx, my);
    if (pointColumnHit !== null && ownedMovablePoints.has(pointColumnHit)) {
      const point = gameState.points[pointColumnHit];
      const topPiece = point.pieces[point.pieces.length - 1];
      if (topPiece && topPiece.color === assignedColor) {
        setSelectedFrom(pointColumnHit);
        return;
      }
    }

    setSelectedFrom(null);
  }, [gameState, matchResult, isSpectatorMode, assignedColor, activePlayerId, localPlayerId, currentTurnColor, boardFlipped, moveDirectionUsedInValidation, destinationPoints, movablePoints, ownedMovablePoints, matchInfo.roomId, mapPointForView]);

  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    handleClick(e);
  }, [handleClick]);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !images || !gameState) return;
    const ctx = canvas.getContext('2d');
    const gs = gameState;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    if (images.background) ctx.drawImage(images.background, 0, 0);
    ctx.save();
    if (boardFlipped) {
      ctx.translate(CANVAS_W, CANVAS_H);
      ctx.rotate(Math.PI);
    }

    for (let loc = 1; loc <= 24; loc++) {
      const point = gs.points[loc];
      const { positions, pieceSize } = getPointStackLayout(mapPointForView(loc), point.pieces.length);
      const { startIndex, visibleCount } = getVisibleStackWindow(point.pieces.length);
      for (let slot = 0; slot < visibleCount; slot += 1) {
        const pieceIndex = startIndex + slot;
        const piece = point.pieces[pieceIndex];
        const [px, py] = positions[slot];
        const isTop = pieceIndex === point.pieces.length - 1;
        const hideAnimatedTarget = moveAnimation
          && moveAnimation.to === loc
          && moveAnimation.color === piece.color
          && isTop;
        let img;
        if (piece.color === 'white') {
          img = gs.diceRolled && gs.currentPlayer === 'white' && isTop && ownedMovablePoints.has(loc)
            ? images.white_highlight
            : images.white_piece;
        } else {
          img = gs.diceRolled && gs.currentPlayer === 'black' && isTop && ownedMovablePoints.has(loc)
            ? images.black_highlight
            : images.black_piece;
        }
        if (img && !hideAnimatedTarget) ctx.drawImage(img, px, py, pieceSize, pieceSize);
      }
    }

    const barWhitePos = getBarPositionsForView('white');
    for (let i = 0; i < gs.bar.white.length && i < 6; i++) {
      const [bx, by] = barWhitePos[i];
      const img = gs.diceRolled && gs.currentPlayer === 'white' && assignedColor === 'white' ? images.white_highlight : images.white_piece;
      if (img) ctx.drawImage(img, bx, by);
    }

    const barBlackPos = getBarPositionsForView('black');
    for (let i = 0; i < gs.bar.black.length && i < 6; i++) {
      const [bx, by] = barBlackPos[i];
      const img = gs.diceRolled && gs.currentPlayer === 'black' && assignedColor === 'black' ? images.black_highlight : images.black_piece;
      if (img) ctx.drawImage(img, bx, by);
    }

    const whiteOffPos = getBearingOffPositionsForView('white');
    for (let i = 0; i < gs.borneOff.white.length && i < 15; i++) {
      const [ox, oy] = whiteOffPos[i];
      if (images.white_beard_off) ctx.drawImage(images.white_beard_off, ox, oy);
    }
    const blackOffPos = getBearingOffPositionsForView('black');
    for (let i = 0; i < gs.borneOff.black.length && i < 15; i++) {
      const [ox, oy] = blackOffPos[i];
      if (images.black_beard_off) ctx.drawImage(images.black_beard_off, ox, oy);
    }

    if (moveAnimation) {
      const progress = Math.max(0, Math.min(1, (performance.now() - moveAnimation.startAt) / moveAnimation.duration));
      const drawX = moveAnimation.startX + ((moveAnimation.endX - moveAnimation.startX) * progress);
      const drawY = moveAnimation.startY + ((moveAnimation.endY - moveAnimation.startY) * progress);
      const drawSize = moveAnimation.startSize + ((moveAnimation.endSize - moveAnimation.startSize) * progress);
      const img = moveAnimation.color === 'white' ? images.white_piece : images.black_piece;
      if (img) ctx.drawImage(img, drawX, drawY, drawSize, drawSize);
    }

    if (gs.gamePhase === 'playing' && gs.currentPlayer && images.turn_light) {
      if (gs.currentPlayer === 'white') ctx.drawImage(images.turn_light, 328, 6);
      else ctx.drawImage(images.turn_light, 490, 6);
    }

    // Dice roll button visuals (online mode)
    if (gs.gamePhase === 'playing' && !gs.diceRolled) {
      if (gs.currentPlayer === 'white') {
        if (images.dice_button) {
          ctx.drawImage(images.dice_button, 840, 440);
        } else {
          ctx.fillStyle = '#7a4c22';
          ctx.fillRect(840, 440, 45, 120);
        }
        ctx.fillStyle = '#f6e7c1';
        ctx.font = 'bold 11px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Dice Roll', 862, 503);
      } else if (gs.currentPlayer === 'black') {
        if (images.player2_dice) {
          ctx.drawImage(images.player2_dice, 3, 420);
        } else {
          ctx.fillStyle = '#7a4c22';
          ctx.fillRect(3, 420, 45, 120);
        }
        ctx.fillStyle = '#f6e7c1';
        ctx.font = 'bold 11px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Dice Roll', 25, 485);
      }
    }

    if (gs.currentPlayer === 'white' && gs.diceRolled) {
      const isDoubleRoll = gs.dice[0] === gs.dice[1];
      const doubleSpent = isDoubleRoll && gs.diceUsed.length === 4 && gs.diceUsed.every(Boolean);
      const d1 = (!isDoubleRoll && gs.diceUsed[0]) || doubleSpent ? images.blank_dice : images[`you_dice_${gs.dice[0]}`];
      const d2 = (!isDoubleRoll && gs.diceUsed[1]) || doubleSpent ? images.blank_dice : images[`you_dice_${gs.dice[1]}`];
      if (d1) ctx.drawImage(d1, 2, 650);
      if (d2) ctx.drawImage(d2, 2, 720);
    } else if (images.blank_dice) {
      ctx.drawImage(images.blank_dice, 2, 650);
      ctx.drawImage(images.blank_dice, 2, 720);
    }

    if (gs.currentPlayer === 'black' && gs.diceRolled) {
      const isDoubleRoll = gs.dice[0] === gs.dice[1];
      const doubleSpent = isDoubleRoll && gs.diceUsed.length === 4 && gs.diceUsed.every(Boolean);
      const d1 = (!isDoubleRoll && gs.diceUsed[0]) || doubleSpent ? images.blank_cpu : images[`cpu_dice_${gs.dice[0]}`];
      const d2 = (!isDoubleRoll && gs.diceUsed[1]) || doubleSpent ? images.blank_cpu : images[`cpu_dice_${gs.dice[1]}`];
      if (d1) ctx.drawImage(d1, 2, 210);
      if (d2) ctx.drawImage(d2, 2, 285);
    } else if (images.blank_cpu) {
      ctx.drawImage(images.blank_cpu, 2, 210);
      ctx.drawImage(images.blank_cpu, 2, 285);
    }

    if (gs.diceRolled && gs.dice[0] === gs.dice[1] && gs.currentPlayer) {
      const remainingMoves = gs.diceUsed.filter((used) => !used).length;
      ctx.fillStyle = 'rgba(0,0,0,0.72)';
      ctx.beginPath();
      ctx.roundRect(332, 20, 236, 34, 8);
      ctx.fill();
      ctx.fillStyle = '#f6e7c1';
      ctx.font = 'bold 18px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`Double ${gs.dice[0]} | Moves left: ${remainingMoves}`, 450, 43);
    }

    if (selectedFrom !== null && movesForSelected.length > 0) {
      for (const move of movesForSelected) {
        if (move.to === 'off') {
          if (isBottomSideColor(gs.currentPlayer) && images.bearing_off_light) ctx.drawImage(images.bearing_off_light, 838, 589);
          if (!isBottomSideColor(gs.currentPlayer) && images.bearing_off_light) ctx.drawImage(images.bearing_off_light, 838, 153);
        } else {
          const displayPoint = mapPointForView(move.to);
          const lp = getPointLightPosition(displayPoint);
          if (displayPoint < 13 && images.destination_light) ctx.drawImage(images.destination_light, lp[0], lp[1]);
          if (displayPoint >= 13 && images.destination_light_bottom) ctx.drawImage(images.destination_light_bottom, lp[0], lp[1]);
        }
      }

      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      for (const move of movesForSelected) {
        if (move.to === 'off') continue;
        const displayPoint = mapPointForView(move.to);
        const lp = getPointLightPosition(displayPoint);
        const labelX = lp[0] + 28;
        const labelY = displayPoint < 13 ? lp[1] - 4 : lp[1] + 68;
        ctx.fillStyle = 'rgba(0,0,0,0.75)';
        const tw = ctx.measureText(`D${move.diceIndex + 1}`).width + 10;
        ctx.beginPath();
        ctx.roundRect(labelX - tw / 2, labelY - 12, tw, 16, 4);
        ctx.fill();
        ctx.fillStyle = '#ffd700';
        ctx.fillText(`D${move.diceIndex + 1}`, labelX, labelY);
      }
    }

    if (gs.noMoveMessage) {
      ctx.fillStyle = 'rgba(21, 10, 6, 0.92)';
      ctx.strokeStyle = '#d6a85f';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(228, 50, 444, 76, 14);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#fff2cf';
      ctx.font = 'bold 24px Trebuchet MS';
      ctx.textAlign = 'center';
      ctx.fillText('No move possible', 450, 82);
      ctx.fillStyle = '#e7c98a';
      ctx.font = 'bold 16px Trebuchet MS';
      ctx.fillText('Turn will pass automatically in 10 seconds', 450, 109);
    }
    ctx.restore();
  }, [images, gameState, movesForSelected, ownedMovablePoints, assignedColor, boardFlipped, mapPointForView, moveAnimation, getBarPositionsForView, getBearingOffPositionsForView, isBottomSideColor]);

  useEffect(() => {
    if (!loaded) return;
    let animId = null;
    const loop = () => {
      render();
      animId = requestAnimationFrame(loop);
    };
    animId = requestAnimationFrame(loop);
    return () => {
      if (animId) cancelAnimationFrame(animId);
    };
  }, [loaded, render]);

  function confirmStillPlaying() {
    socketRef.current?.emit('online:still-playing-yes', { roomId: matchInfo.roomId });
    setStillPrompt(false);
    setNotice('Timer extended for 30 seconds');
  }

  function handleQuitConfirmed() {
    if (isForfeiting) return;
    setIsForfeiting(true);
    socketRef.current?.emit('online:forfeit-round', { roomId: matchInfo.roomId });
    setQuitConfirm(false);
    setNotice('You forfeited this round. Waiting for the result...');
  }

  function handleRollAction() {
    if (!canRollDice) return;
    setDiceTrayRolling(true);
    socketRef.current?.emit('online:match-action', {
      roomId: matchInfo.roomId,
      type: 'roll',
    });
  }

  function handleStartNextRound() {
    socketRef.current?.emit('online:start-next-round', { roomId: matchInfo.roomId });
    // setNotice('Starting next game...');
  }

  function handleOpeningRoll() {
    if (!matchInfo?.roomId || !openingRollModal?.active || openingRollAnimating) return;
    setOpeningRollAnimating(true);
    socketRef.current?.emit('online:opening-roll', { roomId: matchInfo.roomId });
  }

  function sendChat(e) {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text) return;
    if (text.length > 300) {
      setNotice('Chat message must be 300 characters or less.');
      return;
    }
    if (!socketRef.current?.connected) {
      setNotice('Chat is temporarily offline. Please wait for reconnection.');
      return;
    }
    if (!matchInfo?.roomId) {
      setNotice('Match room not available yet. Try again in a moment.');
      return;
    }
    pushIncomingChatMessage({
      roomId: matchInfo.roomId,
      text,
      fromUserId: user.id,
      fromName: user.name || 'You',
      at: new Date().toISOString(),
      pending: true,
    });
    socketRef.current.emit('online:join-match', { roomId: matchInfo.roomId });
    socketRef.current?.emit('online:chat-send', { roomId: matchInfo.roomId, text });
    setChatInput('');
    setNotice('');
  }

  if (!loaded || !gameState) {
    return <div style={{ color: '#f6e7c1', padding: 20 }}>Loading online match...</div>;
  }

  const firstDieCard = (
    <div style={{ ...styles.diceCard, ...styles.diceTrayItem, ...(!gameState?.diceRolled ? styles.diceCardEmpty : {}), ...(visibleDiceValues.blurred ? styles.diceCardBlurred : {}), ...(diceTrayRolling ? styles.diceCardRolling : {}), ...(diceState.firstUsed ? styles.diceCardUsed : {}) }}>
      <div style={styles.dicePipWrap}>{renderDieFace(visibleDiceValues.first)}</div>
    </div>
  );
  const secondDieCard = (
    <div style={{ ...styles.diceCard, ...styles.diceTrayItem, ...(!gameState?.diceRolled ? styles.diceCardEmpty : {}), ...(visibleDiceValues.blurred ? styles.diceCardBlurred : {}), ...(diceTrayRolling ? styles.diceCardRollingDelayed : {}), ...(diceState.secondUsed ? styles.diceCardUsed : {}) }}>
      <div style={styles.dicePipWrap}>{renderDieFace(visibleDiceValues.second)}</div>
    </div>
  );
  const chatPanel = (
    <div style={{ ...styles.chatCard, ...(isMobileViewport ? styles.chatCardMobile : {}) }}>
     
      <div ref={chatScrollRef} className="online-chat-scroll" style={{ ...styles.chatBody, ...(isMobileViewport ? styles.chatBodyMobile : {}) }}>
        {chatMessages.length === 0 && <div style={styles.chatEmpty}>No messages yet.</div>}
        {chatMessages.map((m, idx) => {
          const mine = m.fromUserId === user.id;
          return (
            <div key={`${m.at || idx}-${m.fromUserId || 'user'}-${idx}`} style={{ ...styles.chatMsg, justifyContent: mine ? 'flex-end' : 'flex-start' }}>
              <div style={{ ...styles.chatBubble, ...(mine ? styles.chatBubbleMine : styles.chatBubbleOther) }}>
                <div>{m.text}</div>
                {m.pending && <div style={styles.chatPending}>Sending...</div>}
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={sendChat} style={styles.chatForm}>
        <input
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="Type message..."
          style={styles.chatInput}
        />
        <button type="submit" style={styles.sendBtn}>Send</button>
      </form>
    </div>
  );

  return (
    <div
      style={{
        ...styles.page,
        ...(isMobileViewport ? styles.pageMobile : {}),
        ...(isMobileViewport && mobileTab === 'chat' ? styles.pageMobileChat : {}),
      }}
    >
      <style>{`
        .online-chat-scroll::-webkit-scrollbar {
          width: 0;
          height: 0;
        }
        @keyframes diceTrayRollA {
          0% { transform: translateY(0) rotate(0deg) scale(1); }
          30% { transform: translateY(-8px) rotate(-8deg) scale(1.04); }
          65% { transform: translateY(4px) rotate(7deg) scale(0.98); }
          100% { transform: translateY(0) rotate(0deg) scale(1); }
        }
        @keyframes diceTrayRollB {
          0% { transform: translateY(0) rotate(0deg) scale(1); }
          28% { transform: translateY(-10px) rotate(9deg) scale(1.05); }
          68% { transform: translateY(5px) rotate(-7deg) scale(0.98); }
          100% { transform: translateY(0) rotate(0deg) scale(1); }
        }
        @keyframes rollReadyPulse {
          0% { transform: scale(1); box-shadow: 0 14px 28px rgba(102, 46, 16, 0.35); }
          50% { transform: scale(1.04); box-shadow: 0 0 0 4px rgba(214, 167, 83, 0.18), 0 18px 34px rgba(186, 112, 34, 0.5); }
          100% { transform: scale(1); box-shadow: 0 14px 28px rgba(102, 46, 16, 0.35); }
        }
      `}</style>
      {!isMobileViewport && (
        <aside style={styles.leftPanel}>
          <div style={styles.metaCard}>
            <div style={styles.metaLabel}>Match Status</div>
            <div style={styles.statusInlineRow}>
              <div style={styles.metaValueCompact}>{matchStatusText}</div>
              <div style={styles.statusPillRow}>
                <button type="button" onClick={onToggleFullscreen} style={styles.statusPillBtn}>
                  Full Screen
                </button>
                <button
                  type="button"
                  onClick={() => setQuitConfirm(true)}
                  disabled={isForfeiting}
                  style={{ ...styles.statusPillBtn, ...styles.statusPillDanger, ...(isForfeiting ? styles.buttonDisabled : {}) }}
                >
                  {isForfeiting ? 'Forfeiting...' : 'Quit Match'}
                </button>
              </div>
            </div>
          </div>
          <div style={styles.sessionCard}>
            <div style={styles.sessionCardTop}>
              <div>
                <div style={styles.metaLabel}>Series</div>
                <div style={styles.sessionMetaValue}>First to {sessionInfo?.targetWins || 1} wins</div>
              </div>
              <div style={styles.sessionRoundPill}>Round {sessionInfo?.roundNumber || 1}</div>
            </div>
            <div style={styles.sessionScoreBoard}>
              <div style={styles.sessionPlayerBlock}>
                <div style={styles.sessionPlayerLabel}>You</div>
                <div style={styles.sessionPlayerScore}>{myWins}</div>
              </div>
              <div style={styles.sessionScoreDivider}>:</div>
              <div style={styles.sessionPlayerBlock}>
                <div style={styles.sessionPlayerLabel}>{opponent.name || 'Opponent'}</div>
                <div style={styles.sessionPlayerScore}>{opponentWins}</div>
              </div>
            </div>
          </div>
          <div style={styles.metaCard}>
            <div style={styles.metaLabel}>Stake</div>
            <div style={styles.metaValue}>{matchInfo?.stake || 0} coins</div>
          </div>
          <div style={{ ...styles.diceTrayCard, ...(diceTrayRolling ? styles.diceTrayCardRolling : {}) }}>
            <div style={styles.diceTrayHeader}>
              <div style={styles.diceTrayHeaderText}>
                <div style={styles.diceTrayTurnLabel}>{diceTrayTurnLabel}</div>
                <div style={styles.diceTrayEyebrow}>{diceTrayTitle}</div>
              </div>
              <div style={styles.diceTrayHeaderPills}>
                {gameState?.gamePhase === 'playing' && <div style={styles.diceTrayPill}>{turnElapsedLabel}</div>}
                {gameState?.diceRolled && diceState.isDouble && (
                  <div style={styles.diceTrayPill}>{diceState.remainingMoves} left</div>
                )}
              </div>
            </div>
            <div style={styles.diceTrayBodyDesktop}>
              {firstDieCard}
              {secondDieCard}
              <button
                type="button"
                onClick={handleRollAction}
                disabled={!canRollDice}
                style={{
                  ...styles.rollActionBtn,
                  ...styles.rollActionBtnEqual,
                  ...(canRollDice ? styles.rollActionBtnReady : {}),
                  ...(!canRollDice ? styles.buttonDisabled : {}),
                }}
              >
                Roll Dice
              </button>
            </div>
          </div>
        </aside>
      )}

      <div
        style={{
          ...styles.centerPanel,
          ...(isMobileViewport ? styles.centerPanelMobile : {}),
          ...(isMobileViewport && mobileTab === 'chat' ? styles.centerPanelMobileChat : {}),
        }}
      >
        {isMobileViewport && mobileTab === 'game' && (
        <div style={{ ...styles.gameHeaderCard, ...(isMobileViewport ? styles.gameHeaderCardMobile : {}) }}>
          <div style={styles.gameHeaderStatusBlock}>
            <div style={styles.gameHeaderEyebrow}>Online Match</div>
            <div style={styles.statusInlineRowMobile}>
              <div style={styles.statusTextMobile}>{matchStatusText}</div>
              <div style={styles.statusPillRowMobile}>
                <button type="button" onClick={onToggleFullscreen} style={styles.statusPillBtnMobile}>
                  Full Screen
                </button>
                <button
                  type="button"
                  onClick={() => setQuitConfirm(true)}
                  disabled={isForfeiting}
                  style={{ ...styles.statusPillBtnMobile, ...styles.statusPillDanger, ...(isForfeiting ? styles.buttonDisabled : {}) }}
                >
                  {isForfeiting ? 'Forfeiting...' : 'Quit Match'}
                </button>
              </div>
            </div>
          </div>
          <div style={styles.gameHeaderMetaRow}>
            <div style={styles.stakeBadge}>Stake: {matchInfo?.stake || 0} coins</div>
            <div style={styles.sessionBadge}>First to {sessionInfo?.targetWins || 1}</div>
            <button type="button" onClick={openMobileChatTab} style={styles.mobileChatOpenBtn}>
              Open Chat
              {unseenChatCount > 0 && <span style={styles.mobileChatOpenBadge}>{unseenChatCount}</span>}
            </button>
          </div>
          <div style={styles.mobileSessionCard}>
            <div style={styles.mobileSessionHeader}>
              <div style={styles.mobileSessionTitle}>Series Score</div>
              <div style={styles.mobileSessionRoundPill}>Round {sessionInfo?.roundNumber || 1}</div>
            </div>
            <div style={styles.mobileSessionStrip}>
              <div style={styles.mobileSessionTeam}>
                <span style={styles.mobileSessionName}>You</span>
                <span style={styles.mobileSessionScore}>{myWins}</span>
              </div>
              <div style={styles.mobileSessionVs}>vs</div>
              <div style={styles.mobileSessionTeam}>
                <span style={styles.mobileSessionName}>{opponent.name || 'Opponent'}</span>
                <span style={styles.mobileSessionScore}>{opponentWins}</span>
              </div>
            </div>
          </div>
        </div>
        )}
        {(!isMobileViewport || mobileTab === 'game') && (
          <>
            <div style={{ ...styles.boardStage, ...(isMobileViewport ? styles.boardStageMobile : {}) }}>
              <canvas
                ref={canvasRef}
                width={CANVAS_W}
                height={CANVAS_H}
                onClick={handleClick}
                onTouchStart={handleTouchStart}
                style={{ ...styles.canvas, ...(isMobileViewport ? styles.canvasMobile : {}) }}
              />
            </div>
            {isMobileViewport && mobileTab === 'game' && (
              <div style={styles.mobileDiceBottomCard}>
                <div style={styles.mobileDiceTopHeader}>
                  <div style={styles.mobileDiceTopTextBlock}>
                    <div style={styles.mobileDiceTopTurn}>{diceTrayTurnLabel}</div>
                    <div style={styles.mobileDiceTopTitle}>{diceTrayTitle}</div>
                  </div>
                  <div style={styles.mobileDiceTopPills}>
                    {gameState?.gamePhase === 'playing' && <div style={styles.mobileDiceTopPill}>{turnElapsedLabel}</div>}
                    {gameState?.diceRolled && diceState.isDouble && (
                      <div style={styles.mobileDiceTopPill}>{diceState.remainingMoves} left</div>
                    )}
                  </div>
                </div>
                <div style={styles.mobileDiceTopBody}>
                  {firstDieCard}
                  {secondDieCard}
                  <button
                    type="button"
                    onClick={handleRollAction}
                    disabled={!canRollDice}
                    style={{
                      ...styles.rollActionBtn,
                      ...styles.rollActionBtnMobile,
                      ...styles.rollActionBtnEqual,
                      ...(canRollDice ? styles.rollActionBtnReady : {}),
                      ...(!canRollDice ? styles.buttonDisabled : {}),
                    }}
                  >
                    Roll Dice
                  </button>
                </div>
              </div>
            )}
            {notice && <div style={styles.notice}>{notice}</div>}
          </>
        )}
        {isMobileViewport && mobileTab === 'chat' && (
          <div style={styles.mobileChatScreen}>
            <div style={styles.mobileChatHeader}>
              <button type="button" onClick={openMobileGameTab} style={styles.mobileBackBtn}>Back to Game</button>
              {/* <div style={styles.mobileChatHeaderTitleWrap}>
                <div style={styles.mobileChatHeaderEyebrow}>Online Match</div>
                <div style={styles.mobileChatHeaderTitle}>Match Chat</div>
              </div> */}
            </div>
            {chatPanel}
          </div>
        )}
      </div>

      {!isMobileViewport && (
        <aside style={styles.rightPanel}>
          {chatPanel}
        </aside>
      )}

      {stillPrompt && (
        <Modal>
          <h3 style={{ marginTop: 0 }}>Still Playing?</h3>
          <p>Click Yes within 10 seconds, otherwise you lose by timeout.</p>
          <button type="button" onClick={confirmStillPlaying} style={styles.primaryBtn}>Yes, I am playing</button>
        </Modal>
      )}

      {showResumeOverlay && (
        <Modal>
          <div style={styles.resultModalWrap}>
            <div style={{ ...styles.resultBadge, ...styles.resultBadgeNeutral }}>Reconnecting</div>
            <h2 style={styles.resultTitle}>Returning To Your Match</h2>
            <div style={styles.resultSubtitle}>Connecting with you in your last left match.</div>
            <div style={styles.resultSubtitle}>Please wait 3 seconds...</div>
          </div>
        </Modal>
      )}

      {openingRollModal && !showResumeOverlay && (
        <OnlineOpeningRollModal
          openingRoll={openingRollModal}
          assignedColor={assignedColor}
          starterName={openingRollModal.starter === assignedColor ? localDisplayName : (opponent.name || 'Opponent')}
          whiteName={whitePlayerName}
          blackName={blackPlayerName}
          onRoll={handleOpeningRoll}
          isRolling={openingRollAnimating}
        />
      )}

      {quitConfirm && (
        <Modal>
          <div style={styles.quitModalWrap}>
            <div style={styles.quitModalBadge}>Match Exit</div>
            <div style={styles.quitModalTitle}>Quit Match?</div>
            <div style={styles.quitModalText}>
              Quitting now forfeits this game and gives <b>{opponent.name || 'your opponent'}</b> the round win.
            </div>
            <div style={styles.quitModalStakeRow}>
              <div style={styles.quitModalStakeCard}>
                <div style={styles.quitModalStakeLabel}>Stake</div>
                <div style={styles.quitModalStakeValue}>{matchInfo?.stake || 0} coins</div>
              </div>
              <div style={styles.quitModalStakeCard}>
                <div style={styles.quitModalStakeLabel}>Result</div>
                <div style={styles.quitModalStakeValue}>Round Forfeit</div>
              </div>
            </div>
            <div style={styles.quitModalActions}>
              <button type="button" onClick={() => setQuitConfirm(false)} style={{ ...styles.secondaryBtn, flex: 1 }}>Stay</button>
            <button
              type="button"
              onClick={handleQuitConfirmed}
              disabled={isForfeiting}
              style={{ ...styles.dangerBtn, flex: 1, ...(isForfeiting ? styles.buttonDisabled : {}) }}
            >
              {isForfeiting ? 'Forfeiting...' : 'Forfeit Round'}
            </button>
            </div>
          </div>
        </Modal>
      )}

      {incomingReq && !quitConfirm && !matchResult && !roundResult && (
        <Modal>
          <div style={styles.quitModalWrap}>
            <div style={{ ...styles.quitModalBadge, background: 'rgba(63, 93, 47, 0.25)', color: '#d6ffd1' }}>Coin Request</div>
            <div style={styles.quitModalTitle}>Switch to Coin Match?</div>
            <div style={styles.quitModalText}>
              <strong>{incomingReq.name || 'Player'}</strong> sent a <strong>first to {incomingReq.targetWins || 1}</strong> coin session request
              {' '}with <strong>{incomingReq.stake || 0} coins</strong> stake and <strong>{formatCoins(roundToTwo(Number(incomingReq.stake || 0) * 0.1))} coins</strong> fee.
              <div style={{ marginTop: 10 }}>
                Accepting will forfeit your current friendly match.
              </div>
            </div>
            <div style={styles.quitModalActions}>
              <button type="button" onClick={() => respondIncomingRequest(false)} disabled={isSwitchingMatches} style={{ ...styles.secondaryBtn, flex: 1, ...(isSwitchingMatches ? styles.buttonDisabled : {}) }}>
                Reject
              </button>
              <button
                type="button"
                onClick={() => respondIncomingRequest(true)}
                disabled={isSwitchingMatches}
                style={{ ...styles.primaryBtn, flex: 1, ...(isSwitchingMatches ? styles.buttonDisabled : {}) }}
              >
                {isSwitchingMatches ? 'Switching...' : 'Accept & Switch'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {presence?.paused && isOpponentReconnectPause && !showResumeOverlay && !matchResult && (
        <Modal>
          <div style={styles.resultModalWrap}>
            <div style={{ ...styles.resultBadge, ...styles.resultBadgeNeutral }}>Opponent Left</div>
            <h2 style={styles.resultTitle}>{opponent.name || 'Opponent'} Disconnected</h2>
            <div style={styles.resultSubtitle}>
              Waiting for your opponent to reconnect. The match will continue automatically if they come back in time.
            </div>
            <div style={styles.resultSubtitle}>
              Time left: <b>{reconnectCountdownLabel}</b>
            </div>
            <div style={styles.resultSubtitle}>
              Do not leave. If your opponent does not come back before the time ends, you will win.
            </div>
          </div>
        </Modal>
      )}

      {roundResult && (
        <Modal>
          <div style={styles.resultModalWrap}>
            <div style={{ ...styles.resultBadge, ...styles.resultBadgeNeutral }}>Round Result</div>
            <h2 style={styles.resultTitle}>{roundResult.title}</h2>
            <div style={styles.resultSubtitle}><b>{roundResult.winnerName}</b> won this game.</div>
            <div style={styles.resultSubtitle}>{roundResult.subtitle}</div>
            <button type="button" onClick={handleStartNextRound} style={styles.resultPrimaryBtn}>
              Start Next Game
            </button>
          </div>
        </Modal>
      )}

      {matchResult && (
        <Modal>
          <div style={styles.resultModalWrap}>
            <div
              style={{
                ...styles.resultBadge,
                ...(matchResult.title === 'Victory'
                  ? styles.resultBadgeVictory
                  : matchResult.title === 'Defeat'
                    ? styles.resultBadgeDefeat
                    : styles.resultBadgeNeutral),
              }}
            >
              {matchResult.title}
            </div>
            <h2 style={styles.resultTitle}>{matchResult.title}</h2>
            <div style={styles.resultSubtitle}>{matchResult.subtitle}</div>
            <button type="button" onClick={() => onExitMatch(matchResult.payload)} style={styles.resultPrimaryBtn}>
              {user?.isExternal ? 'Return to Platform' : 'Back to Lobby'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children }) {
  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalCard}>{children}</div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'radial-gradient(circle at top, #3b1f10 0%, #160b06 72%)',
    display: 'grid',
    gridTemplateColumns: 'minmax(280px, 340px) minmax(680px, 900px) minmax(280px, 340px)',
    gap: 12,
    padding: '12px',
    alignItems: 'start',
    justifyContent: 'center',
  },
  pageMobile: {
    display: 'flex',
    width: '100%',
    minHeight: '100vh',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    gap: 8,
    padding: '8px 8px calc(env(safe-area-inset-bottom, 0px) + 92px)',
    alignItems: 'stretch',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  },
  pageMobileChat: {
    height: '100dvh',
    maxHeight: '100dvh',
    overflow: 'hidden',
  },
  centerPanel: {
    gridColumn: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    minHeight: 'calc(100vh - 24px)',
  },
  centerPanelMobile: {
    width: '100%',
    alignItems: 'center',
    minHeight: 0,
    flex: 1,
    overflow: 'hidden',
  },
  centerPanelMobileChat: {
    alignItems: 'stretch',
  },
  leftPanel: {
    gridColumn: 1,
    minHeight: 'calc(100vh - 24px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    paddingTop: 8,
  },
  gameHeaderCard: {
    width: '100%',
    maxWidth: 900,
    display: 'grid',
    gap: 10,
    padding: 12,
    marginBottom: 8,
    borderRadius: 14,
    border: '1px solid rgba(215,177,106,0.25)',
    background: 'linear-gradient(180deg, rgba(49, 25, 13, 0.96), rgba(31, 15, 8, 0.94))',
    boxShadow: '0 10px 24px rgba(0,0,0,0.22)',
  },
  gameHeaderCardMobile: {
    maxWidth: '100%',
    padding: 8,
    gap: 6,
  },
  gameHeaderStatusBlock: {
    display: 'grid',
    gap: 3,
    paddingBottom: 8,
    borderBottom: '1px solid rgba(215,177,106,0.16)',
  },
  statusInlineRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  },
  statusInlineRowMobile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'nowrap',
  },
  statusPillRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'nowrap',
    marginTop: 0,
  },
  statusPillRowMobile: {
    display: 'flex',
    gap: 6,
    flexWrap: 'nowrap',
    marginTop: 0,
  },
  metaValueCompact: {
    color: '#f6e7c1',
    fontSize: 20,
    fontWeight: 800,
    lineHeight: 1.1,
  },
  statusPillBtn: {
    border: '1px solid rgba(215,177,106,0.28)',
    background: 'rgba(47,26,16,0.92)',
    color: '#f6e7c1',
    borderRadius: 999,
    padding: '6px 10px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1,
  },
  statusPillBtnMobile: {
    border: '1px solid rgba(215,177,106,0.28)',
    background: 'rgba(47,26,16,0.92)',
    color: '#f6e7c1',
    borderRadius: 999,
    padding: '5px 9px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 11,
    lineHeight: 1,
  },
  statusPillDanger: {
    background: 'rgba(100,48,48,0.88)',
  },
  gameHeaderMetaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },
  gameHeaderEyebrow: {
    color: '#d7b16a',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    marginBottom: 3,
  },
  gameHeaderActions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  },
  boardTopRow: {
    width: '100%',
    maxWidth: 900,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'stretch',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 10,
  },
  boardTopRowMobile: {
    maxWidth: '100%',
    justifyContent: 'stretch',
    gap: 8,
    marginBottom: 8,
    flex: '0 0 auto',
  },
  diceTrayCard: {
    width: '100%',
    maxWidth: 900,
    display: 'grid',
    gap: 10,
    padding: 12,
    borderRadius: 16,
    border: '1px solid rgba(215,177,106,0.28)',
    background: 'linear-gradient(135deg, rgba(44,24,13,0.96), rgba(26,14,8,0.98))',
    boxShadow: '0 18px 36px rgba(0,0,0,0.28)',
    overflow: 'hidden',
  },
  diceTrayCardRolling: {
    boxShadow: '0 20px 42px rgba(213, 160, 81, 0.22)',
  },
  diceTrayHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  },
  diceTrayHeaderText: {
    display: 'grid',
    gap: 3,
    minWidth: 0,
  },
  diceTrayHeaderPills: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    flexWrap: 'wrap',
  },
  diceTrayTurnLabel: {
    color: '#d7b16a',
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.45,
    lineHeight: 1,
  },
  diceTrayEyebrow: {
    color: '#f6e7c1',
    fontSize: 17,
    fontWeight: 800,
    lineHeight: 1.1,
  },
  diceTrayPill: {
    borderRadius: 999,
    padding: '8px 12px',
    border: '1px solid rgba(215,177,106,0.32)',
    background: 'rgba(122,76,34,0.28)',
    color: '#ffe6b6',
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: 'nowrap',
  },
  diceTrayBody: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  diceTrayBodyDesktop: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    alignItems: 'center',
    justifyContent: 'stretch',
    gap: 12,
  },
  diceCardsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'nowrap',
  },
  diceCard: {
    width: 58,
    height: 58,
    borderRadius: 16,
    border: '1px solid rgba(255,245,228,0.18)',
    background: 'linear-gradient(180deg, #fff6ea 0%, #efd8b7 100%)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.75), 0 12px 28px rgba(0,0,0,0.22)',
    display: 'grid',
    placeItems: 'center',
    position: 'relative',
    transform: 'translateY(0)',
    flexShrink: 0,
  },
  diceTrayItem: {
    width: 58,
    maxWidth: 58,
    justifySelf: 'center',
  },
  diceCardRolling: {
    animation: 'diceTrayRollA 0.45s ease',
  },
  diceCardRollingDelayed: {
    animation: 'diceTrayRollB 0.52s ease',
  },
  diceCardEmpty: {
    opacity: 0.55,
    background: 'linear-gradient(180deg, #f3e6d3 0%, #dbc6a4 100%)',
  },
  diceCardUsed: {
    opacity: 0.45,
    filter: 'grayscale(0.3)',
  },
  dicePipWrap: {
    position: 'relative',
    width: 58,
    height: 58,
  },
  dicePip: {
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: 999,
    background: '#352113',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)',
  },
  rollActionBtn: {
    minWidth: 88,
    border: '1px solid rgba(255,220,160,0.36)',
    background: 'linear-gradient(135deg, #c97a2a, #9a511b)',
    color: '#fff7ef',
    borderRadius: 14,
    padding: '6px 9px',
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 10,
    boxShadow: '0 14px 28px rgba(102, 46, 16, 0.35)',
    flexShrink: 0,
    lineHeight: 1,
  },
  rollActionBtnReady: {
    animation: 'rollReadyPulse 1.1s ease-in-out infinite',
    borderColor: 'rgba(255, 226, 167, 0.88)',
  },
  rollActionBtnEqual: {
    width: '100%',
    minHeight: 58,
  },
  rollActionBtnMobile: {
    width: 'auto',
    minWidth: 110,
  },
  boardStage: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    minHeight: 0,
  },
  boardStageMobile: {
    flex: 1,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginTop: 14,
    minHeight: 0,
  },
  mobileChatWrap: {
    width: '100%',
    maxWidth: 'none',
    marginTop: 0,
    marginBottom: 0,
    minHeight: 0,
    height: '100%',
    display: 'flex',
    paddingBottom: 0,
    boxSizing: 'border-box',
    flex: 1,
  },
  mobileChatScreen: {
    position: 'fixed',
    inset: 0,
    width: '100%',
    height: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '8px 8px calc(env(safe-area-inset-bottom, 0px) + 8px)',
    background: 'radial-gradient(circle at top, #3b1f10 0%, #160b06 72%)',
    overflow: 'hidden',
    zIndex: 40,
  },
  mobileChatHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: 12,
    borderRadius: 14,
    border: '1px solid rgba(215,177,106,0.25)',
    background: 'linear-gradient(180deg, rgba(49, 25, 13, 0.98), rgba(31, 15, 8, 0.96))',
    boxShadow: '0 10px 24px rgba(0,0,0,0.22)',
  },
  mobileBackBtn: {
    border: '1px solid #d7b16a',
    background: '#3a2314',
    color: '#f6e7c1',
    borderRadius: 8,
    padding: '8px 10px',
    cursor: 'pointer',
    fontWeight: 700,
    flexShrink: 0,
  },
  mobileChatHeaderTitleWrap: {
    display: 'grid',
    gap: 2,
    minWidth: 0,
  },
  mobileChatHeaderEyebrow: {
    color: '#d7b16a',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  mobileChatHeaderTitle: {
    color: '#f6e7c1',
    fontSize: 20,
    fontWeight: 800,
    lineHeight: 1.1,
  },
  mobileChatOpenBtn: {
    border: '1px solid #d7b16a',
    background: '#2f1a10',
    color: '#f6e7c1',
    borderRadius: 10,
    padding: '7px 12px',
    cursor: 'pointer',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  mobileChatOpenBadge: {
    minWidth: 18,
    height: 18,
    padding: '0 5px',
    borderRadius: 999,
    background: '#c34d3d',
    color: '#fff7ef',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1,
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
    filter: 'grayscale(0.15)',
  },
  rightPanel: {
    gridColumn: 3,
    width: 320,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    flexShrink: 0,
    minHeight: 'calc(100vh - 24px)',
    maxHeight: 'calc(100vh - 24px)',
    overflow: 'hidden',
  },
  metaCard: {
    background: 'rgba(36, 19, 10, 0.92)',
    border: '1px solid rgba(215,177,106,0.35)',
    borderRadius: 12,
    padding: 14,
    display: 'grid',
    gap: 6,
  },
  metaLabel: {
    color: '#d7b16a',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  metaValue: {
    color: '#f6e7c1',
    fontSize: 24,
    fontWeight: 800,
    lineHeight: 1.15,
  },
  sessionCard: {
    background: 'linear-gradient(180deg, rgba(44, 24, 13, 0.96), rgba(29, 15, 8, 0.96))',
    border: '1px solid rgba(215,177,106,0.35)',
    borderRadius: 12,
    padding: 14,
    display: 'grid',
    gap: 12,
  },
  sessionCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 10,
  },
  sessionMetaValue: {
    color: '#f6e7c1',
    fontSize: 20,
    fontWeight: 800,
    lineHeight: 1.15,
  },
  sessionRoundPill: {
    border: '1px solid rgba(215,177,106,0.35)',
    background: '#2f1a10',
    color: '#f6e7c1',
    borderRadius: 999,
    padding: '6px 10px',
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  sessionScoreBoard: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    gap: 10,
  },
  sessionPlayerBlock: {
    display: 'grid',
    gap: 4,
    textAlign: 'center',
  },
  sessionPlayerLabel: {
    color: '#d6c095',
    fontSize: 12,
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sessionPlayerScore: {
    color: '#f6e7c1',
    fontSize: 30,
    fontWeight: 800,
    lineHeight: 1,
  },
  sessionScoreDivider: {
    color: '#d7b16a',
    fontSize: 20,
    fontWeight: 800,
  },
  statusText: {
    color: '#f0d8a4',
    fontWeight: 800,
    fontSize: 22,
    lineHeight: 1.1,
  },
  statusTextMobile: {
    color: '#f0d8a4',
    fontWeight: 800,
    fontSize: 18,
    lineHeight: 1.05,
  },
  stakeBadge: {
    border: '1px solid #d7b16a',
    background: '#3a2314',
    color: '#f6e7c1',
    borderRadius: 8,
    padding: '6px 10px',
    fontWeight: 700,
    fontSize: 13,
  },
  sessionBadge: {
    border: '1px solid rgba(215,177,106,0.45)',
    background: '#2f1a10',
    color: '#f6e7c1',
    borderRadius: 8,
    padding: '6px 10px',
    fontWeight: 700,
    fontSize: 13,
  },
  mobileSessionStrip: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  mobileSessionCard: {
    display: 'grid',
    gap: 6,
    border: '1px solid rgba(215,177,106,0.18)',
    borderRadius: 10,
    padding: 8,
    background: 'rgba(27, 14, 8, 0.55)',
  },
  mobileSessionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  mobileSessionTitle: {
    color: '#d7b16a',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  mobileSessionRoundPill: {
    border: '1px solid rgba(215,177,106,0.35)',
    background: '#2f1a10',
    color: '#f6e7c1',
    borderRadius: 999,
    padding: '4px 8px',
    fontSize: 10,
    fontWeight: 700,
  },
  mobileSessionTeam: {
    display: 'grid',
    gap: 2,
    textAlign: 'center',
    flex: 1,
  },
  mobileSessionName: {
    color: '#d6c095',
    fontSize: 11,
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mobileSessionScore: {
    color: '#f6e7c1',
    fontSize: 20,
    fontWeight: 800,
  },
  mobileSessionVs: {
    color: '#d7b16a',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
  },
  mobileDiceTopCard: {
    display: 'grid',
    gap: 8,
    border: '1px solid rgba(215,177,106,0.18)',
    borderRadius: 10,
    padding: 8,
    background: 'rgba(27, 14, 8, 0.55)',
  },
  mobileDiceBottomCard: {
    width: '100%',
    maxWidth: 900,
    display: 'grid',
    gap: 8,
    marginTop: 8,
    padding: 8,
    borderRadius: 10,
    border: '1px solid rgba(215,177,106,0.18)',
    background: 'rgba(27, 14, 8, 0.55)',
  },
  mobileDiceTopBody: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    alignItems: 'center',
    justifyContent: 'stretch',
    gap: 8,
  },
  mobileDiceTopHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },
  mobileDiceTopTextBlock: {
    display: 'grid',
    gap: 2,
    minWidth: 0,
  },
  mobileDiceTopPills: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
    flexWrap: 'wrap',
  },
  mobileDiceTopTurn: {
    color: '#d7b16a',
    fontSize: 10,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    lineHeight: 1,
  },
  mobileDiceTopTitle: {
    color: '#f6e7c1',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.1,
  },
  mobileDiceTopPill: {
    borderRadius: 999,
    padding: '4px 8px',
    border: '1px solid rgba(215,177,106,0.28)',
    background: 'rgba(122,76,34,0.24)',
    color: '#ffe6b6',
    fontSize: 10,
    fontWeight: 800,
  },
  quitBtn: {
    border: '1px solid #d7b16a',
    background: '#643030',
    color: '#f6e7c1',
    borderRadius: 8,
    padding: '8px 12px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  fullscreenBtn: {
    border: '1px solid #d7b16a',
    background: '#7a4c22',
    color: '#f6e7c1',
    borderRadius: 8,
    padding: '8px 12px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  canvas: {
    maxWidth: '100%',
    maxHeight: 'calc(100vh - 36px)',
    cursor: 'pointer',
    borderRadius: 8,
    boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
    WebkitTapHighlightColor: 'transparent',
    WebkitUserSelect: 'none',
    userSelect: 'none',
    touchAction: 'manipulation',
    outline: 'none',
  },
  canvasMobile: {
    width: '100%',
    maxWidth: '100vw',
    height: 'auto',
    minHeight: 'auto',
    maxHeight: 'none',
    margin: '0 auto',
    display: 'block',
  },
  notice: {
    marginTop: 8,
    color: '#ffd48a',
    fontSize: 14,
  },
  chatCard: {
    minHeight: 220,
    maxHeight: '100%',
    background: 'rgba(36, 19, 10, 0.92)',
    border: '1px solid rgba(215,177,106,0.35)',
    borderRadius: 12,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  chatCardMobile: {
    minHeight: 0,
    height: '100%',
    width: '100%',
    padding: 8,
    flex: 1,
    boxSizing: 'border-box',
    maxHeight: '100%',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
  },
  chatTitle: {
    color: '#f6e7c1',
    fontWeight: 700,
    marginBottom: 8,
  },
  chatBody: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    border: '1px solid rgba(215,177,106,0.18)',
    borderRadius: 8,
    padding: 8,
    marginBottom: 8,
    background: 'rgba(22, 10, 5, 0.5)',
  },
  chatBodyMobile: {
    minHeight: 0,
    maxHeight: 'none',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  chatEmpty: {
    color: '#cdb88f',
  },
  chatMsg: {
    display: 'flex',
    marginBottom: 6,
  },
  chatBubble: {
    maxWidth: '88%',
    color: '#f6e7c1',
    border: '1px solid rgba(215,177,106,0.25)',
    borderRadius: 8,
    padding: '6px 8px',
  },
  chatBubbleMine: {
    background: '#7a4c22',
  },
  chatBubbleOther: {
    background: '#3a2314',
  },
  chatPending: {
    color: '#e7d3ac',
    fontSize: 10,
    marginTop: 4,
    opacity: 0.8,
  },
  chatForm: {
    display: 'flex',
    gap: 8,
    alignItems: 'stretch',
    flexWrap: 'nowrap',
    flexShrink: 0,
    marginTop: 'auto',
    paddingBottom: 4,
  },
  chatInput: {
    flex: 1,
    minWidth: 0,
    borderRadius: 8,
    border: '1px solid rgba(215,177,106,0.35)',
    background: '#2a170f',
    color: '#f6e7c1',
    padding: '8px 10px',
  },
  sendBtn: {
    border: '1px solid #d7b16a',
    background: '#7a4c22',
    color: '#f6e7c1',
    borderRadius: 8,
    padding: '8px 12px',
    cursor: 'pointer',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.32)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4000,
  },
  modalCard: {
    width: 420,
    maxWidth: '92vw',
    background: 'rgba(33, 18, 11, 0.97)',
    border: '1px solid rgba(215,177,106,0.45)',
    borderRadius: 12,
    color: '#f6e7c1',
    padding: 16,
    boxShadow: '0 12px 35px rgba(0,0,0,0.45)',
  },
  resultModalWrap: {
    display: 'grid',
    gap: 14,
    textAlign: 'center',
  },
  quitModalWrap: {
    display: 'grid',
    gap: 14,
  },
  quitModalBadge: {
    justifySelf: 'start',
    padding: '6px 14px',
    borderRadius: 999,
    border: '1px solid rgba(215,177,106,0.45)',
    background: 'rgba(100, 48, 48, 0.35)',
    color: '#ffcdc7',
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  quitModalTitle: {
    color: '#f6e7c1',
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1.1,
  },
  quitModalText: {
    fontSize: 15,
    lineHeight: 1.55,
    color: '#f8ead0',
    padding: '12px 14px',
    borderRadius: 12,
    border: '1px solid rgba(215,177,106,0.18)',
    background: 'rgba(58, 35, 20, 0.62)',
  },
  quitModalStakeRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
  },
  quitModalStakeCard: {
    borderRadius: 12,
    border: '1px solid rgba(215,177,106,0.28)',
    background: 'linear-gradient(180deg, rgba(71,43,23,0.98), rgba(28,15,9,0.98))',
    padding: '12px 14px',
    boxShadow: '0 10px 22px rgba(0,0,0,0.18)',
  },
  quitModalStakeLabel: {
    color: '#f0c36a',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.9,
    marginBottom: 6,
  },
  quitModalStakeValue: {
    color: '#f6e7c1',
    fontSize: 17,
    fontWeight: 800,
  },
  quitModalActions: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  resultBadge: {
    justifySelf: 'center',
    padding: '6px 14px',
    borderRadius: 999,
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    border: '1px solid rgba(215,177,106,0.45)',
  },
  resultBadgeVictory: {
    background: 'rgba(63, 93, 47, 0.35)',
    color: '#c8f0b8',
  },
  resultBadgeDefeat: {
    background: 'rgba(100, 48, 48, 0.35)',
    color: '#ffcdc7',
  },
  resultBadgeNeutral: {
    background: 'rgba(122, 76, 34, 0.35)',
    color: '#ffd48a',
  },
  resultTitle: {
    margin: 0,
    color: '#f6e7c1',
    fontSize: 30,
    fontWeight: 800,
  },
  resultSubtitle: {
    color: '#e7d3ac',
    fontSize: 15,
    lineHeight: 1.5,
    padding: '10px 12px',
    borderRadius: 10,
    background: 'rgba(58, 35, 20, 0.62)',
    border: '1px solid rgba(215,177,106,0.18)',
  },
  resultPrimaryBtn: {
    border: '1px solid #d7b16a',
    background: 'linear-gradient(135deg, #8a5a2b, #6a4820)',
    color: '#f6e7c1',
    borderRadius: 10,
    padding: '10px 16px',
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 15,
    width: '100%',
  },
  primaryBtn: {
    border: '1px solid #d7b16a',
    background: '#7a4c22',
    color: '#f6e7c1',
    borderRadius: 8,
    padding: '8px 12px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  secondaryBtn: {
    border: '1px solid #d7b16a',
    background: '#3a2314',
    color: '#f6e7c1',
    borderRadius: 8,
    padding: '8px 12px',
    cursor: 'pointer',
  },
  dangerBtn: {
    border: '1px solid #d7b16a',
    background: '#643030',
    color: '#f6e7c1',
    borderRadius: 8,
    padding: '8px 12px',
    cursor: 'pointer',
    fontWeight: 700,
  },
};
